import React, { useState } from "react";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import { PackageDef } from "./packages";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";

interface Props {
  pkg: PackageDef;
  onClose(): void;
}

export default function PackageSetupWizard({ pkg, onClose }: Props) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const api = useApi();

  async function handleDone() {
    // Fire and forget — trigger the onboarding drip email sequence
    try {
      await api.admin.post("PackageWizard/complete", { packageId: pkg.id });
    } catch {
      // Silent — drip failure must never block the user
    }
    onClose();
  }

  const currentStep = pkg.wizardSteps[step];
  const isLast = step === pkg.wizardSteps.length - 1;
  const progress = Math.round(((step + 1) / pkg.wizardSteps.length) * 100);

  function getStepLink(componentKey: string): { label: string; to: string } | null {
    const map: Record<string, { label: string; to: string }> = {
      // LeadPro
      "lead-pro-crm": { label: "Go to Integrations", to: "/integrations" },
      "lead-pro-deploy": { label: "Go to Deployment", to: "/deployment" },
      // TextConnect
      "text-connect-sms": { label: "Go to SMS Settings", to: "/sms" },
      "text-connect-widget": { label: "Go to Page Widgets", to: "/page-widgets" },
      // SupportDesk
      "support-desk-team": { label: "Go to Teams", to: "/teams" },
      "support-desk-chat": { label: "Go to Deployment", to: "/deployment" },
      "support-desk-kb": { label: "Go to Knowledge Base", to: "/kb/articles" },
      // AppointmentPro
      "appt-pro-calendar": { label: "Go to Integrations", to: "/integrations" },
      // CommerceConnect
      "commerce-store": { label: "Go to Integrations", to: "/integrations" },
      "commerce-widget": { label: "Go to Page Widgets", to: "/page-widgets" }
    };
    return map[componentKey] ?? null;
  }

  const actionLink = getStepLink(currentStep.componentKey);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-stone-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header with accent */}
        <div className="px-6 py-5 flex items-center gap-3" style={{ background: pkg.accentColor }}>
          <div className="w-10 h-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
            <i className={`fa-solid ${pkg.icon} text-white text-lg`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-bold text-lg leading-tight">{pkg.name} Setup</div>
            <div className="text-white text-opacity-80 text-sm truncate">{pkg.tagline}</div>
          </div>
          <button
            onClick={onClose}
            className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity flex-shrink-0"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-1 transition-all duration-300"
            style={{ width: `${progress}%`, background: pkg.accentColor }}
          />
        </div>

        {/* Step counter */}
        <div className="px-6 pt-4 flex items-center gap-2">
          {pkg.wizardSteps.map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-2"
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  i < step
                    ? "bg-green-500 text-white"
                    : i === step
                    ? "text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
                style={i === step ? { background: pkg.accentColor } : {}}
              >
                {i < step ? <i className="fa-solid fa-check text-xs" /> : i + 1}
              </div>
              {i < pkg.wizardSteps.length - 1 && (
                <div className={`flex-1 h-0.5 w-6 ${i < step ? "bg-green-500" : "bg-gray-200"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="px-6 py-5">
          <div className="text-base font-semibold mb-2">{currentStep.title}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {currentStep.description}
          </div>

          {/* Action link for steps that have a direct destination */}
          {actionLink && !isLast && (
            <button
              onClick={() => { navigate(actionLink.to); onClose(); }}
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg border-2 transition-all hover:opacity-80"
              style={{ borderColor: pkg.accentColor, color: pkg.accentColor }}
            >
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
              {actionLink.label}
            </button>
          )}

          {/* Done state */}
          {isLast && (
            <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-100">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <i className="fa-solid fa-check text-white text-sm" />
              </div>
              <div className="text-sm text-green-700 font-medium">Setup complete — {pkg.name} is active on your account.</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between">
          <div className="text-xs text-gray-400">Step {step + 1} of {pkg.wizardSteps.length}</div>
          <div className="flex gap-2">
            {step > 0 && !isLast && (
              <SecondaryBlueButton label="Back" onClick={() => setStep(s => s - 1)} />
            )}
            {!isLast && (
              <>
                <SecondaryBlueButton label="Skip step" onClick={() => setStep(s => s + 1)} />
                <PrimaryBlueButton
                  label="Next"
                  onClick={() => setStep(s => s + 1)}
                />
              </>
            )}
            {isLast && (
              <PrimaryBlueButton label="Done" onClick={handleDone} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
